import React, { createContext } from 'react';

export interface Report {
  id: string;
  createdByUserId: number;
  status: Status;
  queryType: QueryType;
  error?: string;
  summary?: string;
  categories: Category[];
  text: string;
  estimatedPercentComplete: number;
  classiferModel?: string;
  relatedQueries?: string[];
  allWorks: LabeledWork[];
  originalQuery?: string;
  previousRun?: ReportSummary;
}

// Threshold at which we can start showing the UI elements
// that are meant to be shown on completed reports
export const PercentCompleteThreshold = 90;

export type KeywordSuggestedQuestions = {
  queryType: 'keyword_search' | 'other';
  questions: string[];
};

export type SortMode = 'date' | 'citations' | 'relevance_score';

export type Status =
  | 'generating_rag_summary'
  | 'finding_key_claims'
  | 'hallucinating_refutations'
  | 'searching_for_candidates'
  | 'labeling_candidate_works'
  | 'complete'
  | 'error';

export type QueryType =
  | 'yes_no_question'
  | 'question'
  | 'claim'
  | 'research_article'
  | 'keyword_search'
  | 'data_value_question'
  | 'instruction_search'
  | 'instruction_search_relevance_score'
  | 'hypothesis_survey'
  | 'other';

export interface Category {
  id: number;
  work_ids?: Array<number | string>;
  short_description?: string;
  detailed_description?: string;
  supportingWorks: LabeledWork[];
  refutingWorks: LabeledWork[];
  percentSupportingWorks: number;
  expandedClaim?: ExpandedClaim;
}

export type WorkType = 'academia_work' | 'openalex_work' | 'aact_study';

export interface Work {
  id: number | string;
  title: string;
  workType: WorkType;
  abstract: string;
  url: string;
  oaUrlType?: 'pdf' | 'html';
  publication?: string;
  publicationYear?: number;
  citationCount?: number;
  doi?: string;
  authors?: {
    id?: string;
    name: string;
    affiliation: Institution | null;
  }[];
  citationCountsByYear?: {
    year: number;
    count: number;
  }[];
  funders?: Funder[];
  publisher?: string;
  version?: number;
}

export type Funder = {
  funderId: string;
  funderName: string;
};

export type Institution = {
  id: string;
  name: string;
  latitude?: number;
  longitude?: number;
};

export type SupportLevel =
  | 'strongly_contradict'
  | 'somewhat_contradict'
  | 'neither'
  | 'somewhat_support'
  | 'strongly_support';

export interface LabeledWork {
  description: string;
  supportiveness?: number;
  citation_number?: number;
  support_level?: SupportLevel;
  work: Work;
  full_text_answers?: FullTextAnswers;
  relevance_score?: number;
}

export interface ExpandedClaim {
  pro: string;
  con: string;
}

export interface FullTextAnswers {
  summary?: string;
  quotes?: string[];
  key_results: string;
  key_problem_or_question: string;
  proposed_solution: string;
  methodology: string;
  future_directions: string;
  positive_or_negative_result:
    | 'positive_results'
    | 'negative_results'
    | 'mixed_results';
}

export interface WorkWithFullTextAnswers {
  work: Work;
  full_text_answers: FullTextAnswers;
}

export interface ReportSummary {
  id: string;
  text: string;
  queryType: QueryType;
  hasFeedback: boolean;
  workCount: number;
}

export interface ReportHistoryPage {
  runs: ReportSummary[];
  prev_page_offset: number;
  next_page_offset: number;
  has_next_page: boolean;
  has_prev_page: boolean;
}

export interface ReportHistoryPageCursor {
  before?: number;
  after?: number;
}

export interface RagCitation {
  span: [number, number];
  references: Record<number, string | number>;
}

interface NavigationContextType {
  inputForm: React.ReactNode;
  redirectToReport(reportId: string, replaceHistory?: boolean): void;
  redirectHome(): void;
  updateInputText(text: string): void;
  submitInputText(text: string, relatedReportId?: string): void;
  submittedText: string;
  updateSubmittedText(text: string): void;
  inputDisabled: boolean;
  lastCreatedReportId: string;
  urlBasename: string;
  demoKey?: string;
  usersPreviousReport: ReportSummary | null;
  reportLayout: 'normal' | 'top_down';
}

export const NavigationContext = createContext<NavigationContextType>({
  inputForm: null,
  redirectToReport: () => {},
  redirectHome: () => {},
  updateInputText: () => {},
  submitInputText: () => {},
  submittedText: '',
  updateSubmittedText: () => {},
  inputDisabled: false,
  lastCreatedReportId: '',
  urlBasename: '/answers',
  demoKey: undefined,
  usersPreviousReport: null,
  reportLayout: 'normal',
});

export const ReportHistoryContext = createContext<{
  page: ReportHistoryPage;
  isLoading: boolean;
  onNextPage: () => void;
  onPreviousPage: () => void;
  refresh: () => void;
}>({
  page: {
    runs: [],
    prev_page_offset: 0,
    next_page_offset: 0,
    has_next_page: false,
    has_prev_page: false,
  },
  isLoading: false,
  onNextPage: () => {},
  onPreviousPage: () => {},
  refresh: () => {},
});

export const ReportContext = createContext<{
  report: Report | null;
  sortMode: SortMode;
  setSortMode: (sortMode: SortMode) => void;
  tryPolling: (newReport: Report) => void;
  hasScrolledPastRelatedQueries: boolean;
  setHasScrolledPastRelatedQueries: (hasScrolledPast: boolean) => void;
  justSubmitted: boolean;
  ignoreSortMode: boolean;
  setIgnoreSortMode: (value: boolean) => void;
}>({
  report: null,
  sortMode: 'date',
  setSortMode: () => {},
  tryPolling: () => {},
  hasScrolledPastRelatedQueries: false,
  setHasScrolledPastRelatedQueries: () => {},
  justSubmitted: false,
  ignoreSortMode: false,
  setIgnoreSortMode: () => {},
});

export function reportHasAnyResults(report: Report | null): boolean {
  if (!report) {
    return false;
  }

  for (const category of report.categories) {
    if (category.supportingWorks.length > 0) {
      return true;
    }
  }

  return false;
}

export function toReportWithOrderedCategories(report: Report): Report {
  const { status, estimatedPercentComplete, categories, queryType } = report;

  const isComplete = ['complete', 'error'].includes(status);
  const isAlmostComplete =
    isComplete || estimatedPercentComplete >= PercentCompleteThreshold;

  if (!isAlmostComplete) {
    return report;
  }

  const orderedCategories = categories.slice().sort((catA, catB) => {
    if (!['yes_no_question', 'claim'].includes(queryType)) {
      return catB.supportingWorks.length - catA.supportingWorks.length;
    }

    // For claims and yes/no questions we want to show all "pro" categories first
    // followed by all "con" categories ranked by work count
    const catADescription = catA.short_description ?? '';
    const catBDescription = catB.short_description ?? '';
    const descriptionComparison =
      catBDescription.localeCompare(catADescription);

    if (descriptionComparison === 0) {
      return catB.supportingWorks.length - catA.supportingWorks.length;
    } else {
      return descriptionComparison;
    }
  });

  return {
    ...report,
    categories: orderedCategories,
  };
}
